/* COLESICE theme — core script.
   Loaded once in the <head> as a module. Section scripts (component-*.js, section-*.js) are modules too and run
   after this file, so they can use window.Theme (helpers, cart API, events) directly.

   Events dispatched on document:
     cart:change   { cart, source, sections }   after the cart changed through Theme.cart
     cart:refresh  {}                           ask cart UIs to reload themselves (e.g. after an app changed the cart)
     variant:change { sectionId, variant }      dispatched by product forms
     dialog:open / dialog:close { dialog }      when a <dialog> opens or closes
*/

const Theme = (window.Theme = window.Theme || {});
const config = window.theme || { routes: { root: '/' }, strings: {} };
Theme.config = config;

/* ---------- Helpers ---------- */
Theme.debounce = (fn, wait = 250) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

Theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

// Fetches a page with only the given sections rendered (Section Rendering API). Returns { id: html }.
Theme.fetchSections = async (url, sectionIds) => {
  const u = new URL(url, location.href);
  u.searchParams.set('sections', [].concat(sectionIds).join(','));
  const res = await fetch(u, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Section request failed (${res.status})`);
  return res.json();
};

// Fetches a page as a document, optionally only one section (section_id).
Theme.fetchDocument = async (url, sectionId) => {
  const u = new URL(url, location.href);
  if (sectionId) u.searchParams.set('section_id', sectionId);
  const res = await fetch(u);
  return Theme.parseHTML(await res.text());
};

// Replaces the inner HTML of every element matching selector with the matching element of a new document.
Theme.replaceContent = (fromDoc, selector, root = document) => {
  const next = fromDoc.querySelector(selector);
  const current = root.querySelector(selector);
  if (next && current) current.innerHTML = next.innerHTML;
  return current;
};

// Money drawn by script. The pattern comes from Liquid (config.moneySample = 1,234,567.89 formatted by Shopify in the
// visitor's own currency), so the symbol and separators are exactly what the shelf prices show. Never a fixed currency.
let moneyPattern;
const readMoneySample = (sample) => {
  const m = typeof sample === 'string' && sample.match(/^(.*?)(\d[\d.,'\u2019\u00a0\u202f ]*\d)(.*)$/);
  if (!m) return null;
  const [, prefix, num, suffix] = m;
  const seps = num.replace(/\d/g, '');
  const last = seps.slice(-1);
  const tail = num.slice(num.lastIndexOf(last) + 1);
  const hasDecimals = !!last && tail.length === 2;
  const decimal = hasDecimals ? last : '';
  const thousands = (hasDecimals ? seps.slice(0, -1) : seps).charAt(0);
  return { prefix, suffix, decimal, thousands, precision: hasDecimals ? 2 : 0 };
};
Theme.formatMoney = (cents, format) => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = Number(cents) || 0;
  const delimit = (n, precision, thousands = ',', decimal = '.') => {
    const [whole, frac] = (n / 100).toFixed(precision).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  if (!format) {
    if (moneyPattern === undefined) moneyPattern = readMoneySample(config.moneySample);
    if (moneyPattern) return moneyPattern.prefix + delimit(cents, moneyPattern.precision, moneyPattern.thousands, moneyPattern.decimal) + moneyPattern.suffix;
    const active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency;
    if (active) {
      try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: active }).format(cents / 100); } catch (e) { /* unknown code */ }
      return `${delimit(cents, 2)} ${active}`;
    }
    format = config.moneyFormat || '{{amount}}';
  }
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return delimit(cents, 2);
      case 'amount_no_decimals': return delimit(cents, 0);
      case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
      case 'amount_with_space_separator': return delimit(cents, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return delimit(cents, 0, ' ');
      case 'amount_with_period_and_space_separator': return delimit(cents, 2, ' ', '.');
      default: return delimit(cents, 2);
    }
  });
};

Theme.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));

/* ---------- Cart API ---------- */
// Every element with data-cart-section="<section id>" is re-rendered after a cart change.
const cartSectionIds = () => [...new Set([...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection))];

let ownCartRequest = 0;
async function cartRequest(url, body, source) {
  ownCartRequest++;
  try {
    const sections = cartSectionIds();
    const isForm = body instanceof FormData;
    if (sections.length) {
      if (isForm) { body.append('sections', sections.join(',')); body.append('sections_url', location.pathname); }
      else Object.assign(body, { sections: sections.join(','), sections_url: location.pathname });
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      const error = new Error(data.description || data.message || config.strings.cartError || 'Error');
      error.data = data;
      throw error;
    }
    const cart = data.items && data.token ? data : await Theme.cart.get();
    Theme.renderCartSections(data.sections);
    Theme.emit('cart:change', { cart, source, sections: data.sections || {}, response: data });
    return data;
  } finally {
    setTimeout(() => ownCartRequest--, 50);
  }
}

Theme.cart = {
  get: () => fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
  add: (formDataOrItems, source = 'cs-buy-form') => {
    const body = formDataOrItems instanceof FormData ? formDataOrItems : { items: [].concat(formDataOrItems) };
    return cartRequest(`${config.routes.cartAdd}.js`, body, source);
  },
  change: (line, quantity, source = 'cart') => cartRequest(`${config.routes.cartChange}.js`, { line, quantity }, source),
  update: (payload, source = 'cart') => cartRequest(`${config.routes.cartUpdate}.js`, payload, source),
};

// Puts freshly rendered section HTML (from a cart response) into the page.
Theme.renderCartSections = (sections) => {
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const doc = Theme.parseHTML(html);
    document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
      const key = target.dataset.cartPart;
      const source = key ? doc.querySelector(`[data-cart-part="${key}"]`) : doc.querySelector(`[data-cart-section="${id}"]`);
      if (source) target.innerHTML = source.innerHTML;
    });
  }
};

// Apps that change the cart with their own requests: ask the cart UIs to refresh.
(function watchForeignCartChanges() {
  const isCartUrl = (url) => /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(String(url));
  const ping = Theme.debounce(() => { if (!ownCartRequest) Theme.emit('cart:refresh'); }, 150);
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const p = origFetch.apply(this, args);
    try { const url = args[0] instanceof Request ? args[0].url : args[0]; if (isCartUrl(url)) p.then(ping, () => {}); } catch (e) {}
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isCartUrl(url)) this.addEventListener('load', ping);
    return origOpen.call(this, method, url, ...rest);
  };
})();

// Cart count bubbles anywhere on the page: <span data-cart-count>3</span>
const setCartCount = (count) => document.querySelectorAll('[data-cart-count]').forEach((el) => {
  el.textContent = count;
  el.hidden = !count;
});
document.addEventListener('cart:change', (e) => { if (e.detail.cart) setCartCount(e.detail.cart.item_count); });
document.addEventListener('cart:refresh', () => Theme.cart.get().then((cart) => setCartCount(cart.item_count)));

/* ---------- Dialogs (drawers, modals) ---------- */
// <button data-dialog-open="cart-drawer"> opens <dialog id="cart-drawer">; [data-dialog-close] closes the nearest dialog.
const lastFocus = new WeakMap();
// Keyboard users get focus on the first control (with its focus ring); pointer users get focus on the dialog box itself,
// so no ring shows around the close button when a drawer opens from a click or tap (live behaviour).
let keyboardInput = false;
document.addEventListener('keydown', () => { keyboardInput = true; }, true);
document.addEventListener('pointerdown', () => { keyboardInput = false; }, true);
Theme.openDialog = (dialog, opener) => {
  if (typeof dialog === 'string') dialog = document.getElementById(dialog);
  if (!dialog || dialog.open) return;
  lastFocus.set(dialog, opener || document.activeElement);
  if (dialog.classList.contains('popover-dialog')) dialog.show();
  else { dialog.showModal(); document.body.classList.add('is-locked'); }
  if (!keyboardInput) {
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  }
  Theme.emit('dialog:open', { dialog });
};
Theme.closeDialog = (dialog) => {
  if (typeof dialog === 'string') dialog = document.getElementById(dialog);
  if (!dialog || !dialog.open) return;
  dialog.close();
};
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const target = document.getElementById(opener.dataset.dialogOpen);
    if (target) { event.preventDefault(); Theme.openDialog(target, opener); }
    return;
  }
  const closer = event.target.closest('[data-dialog-close]');
  if (closer) { Theme.closeDialog(closer.closest('dialog')); return; }
  // click on the backdrop (outside the dialog box)
  if (event.target instanceof HTMLDialogElement && event.target.open && !event.target.classList.contains('popover-dialog')) {
    const r = event.target.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside || event.target.classList.contains('drawer--full')) Theme.closeDialog(event.target);
  }
});
document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;
  if (!document.querySelector('dialog[open]:not(.popover-dialog)')) document.body.classList.remove('is-locked');
  const back = lastFocus.get(dialog);
  if (back && back.isConnected) back.focus({ preventScroll: true });
  Theme.emit('dialog:close', { dialog });
}, true);

/* ---------- Quantity stepper ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-step]');
      if (!btn || !this.input) return;
      e.preventDefault();
      const step = Number(btn.dataset.step);
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + step));
      if (String(next) !== this.input.value) {
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}
if (!customElements.get('cs-qty')) customElements.define('cs-qty', QuantityInput);

/* ---------- Carousel ---------- */
// <cs-carousel> wraps a .scroller track ([data-track]) of slides. Optional inside: [data-prev], [data-next],
// [data-dots] (filled automatically). Attributes: autoplay="6" (seconds), loop, align="center".
class ScrollCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]') || this.firstElementChild;
    if (!this.track) return;
    this.prevButtons = [...this.querySelectorAll('[data-prev]')];
    this.nextButtons = [...this.querySelectorAll('[data-next]')];
    this.dots = this.querySelector('[data-dots]');
    this.prevButtons.forEach((b) => b.addEventListener('click', () => this.go(-1)));
    this.nextButtons.forEach((b) => b.addEventListener('click', () => this.go(1)));
    this.onScroll = Theme.debounce(() => this.update(), 60);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => { this.buildDots(); this.update(); });
    this.resizeObserver.observe(this.track);
    this.buildDots();
    this.update();
    const seconds = Number(this.getAttribute('autoplay'));
    if (seconds > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => { if (!this.paused && this.isInView) this.go(1, true); }, seconds * 1000);
      ['pointerenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => (this.paused = true), { passive: true }));
      ['pointerleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
      this.io = new IntersectionObserver(([entry]) => (this.isInView = entry.isIntersecting));
      this.io.observe(this);
    }
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    this.resizeObserver?.disconnect();
    this.io?.disconnect();
  }
  get slides() { return [...this.track.children].filter((el) => el.offsetParent !== null || el.getClientRects().length); }
  get pageCount() {
    const { scrollWidth, clientWidth } = this.track;
    const slide = this.slides[0];
    if (!slide || scrollWidth <= clientWidth + 2) return 1;
    const per = Math.max(1, Math.round(clientWidth / slide.getBoundingClientRect().width));
    return Math.max(1, this.slides.length - per + 1);
  }
  get index() {
    const left = Math.abs(this.track.scrollLeft);
    const slides = this.slides;
    let best = 0, bestDist = Infinity;
    const start = slides[0] ? slides[0].offsetLeft : 0;
    slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - start - left); if (d < bestDist) { bestDist = d; best = i; } });
    return best;
  }
  goTo(i, smooth = true) {
    const slides = this.slides;
    if (!slides.length) return;
    const n = Math.max(0, Math.min(i, slides.length - 1));
    const start = slides[0].offsetLeft;
    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: dir * (slides[n].offsetLeft - start), behavior: smooth ? 'smooth' : 'auto' });
  }
  go(step, wrap = this.hasAttribute('loop')) {
    let next = this.index + step;
    const last = this.pageCount - 1;
    if (next > last) next = wrap ? 0 : last;
    if (next < 0) next = wrap ? last : 0;
    this.goTo(next);
  }
  buildDots() {
    if (!this.dots) return;
    const count = this.pageCount;
    if (this.dots.childElementCount === count) return;
    this.dots.innerHTML = '';
    this.dots.hidden = count < 2;
    for (let i = 0; i < count; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dots__dot';
      b.setAttribute('aria-label', `${i + 1} / ${count}`);
      b.addEventListener('click', () => this.goTo(i));
      this.dots.append(b);
    }
  }
  update() {
    const i = this.index;
    const last = this.pageCount - 1;
    const loop = this.hasAttribute('loop');
    this.prevButtons.forEach((b) => (b.disabled = !loop && i <= 0));
    this.nextButtons.forEach((b) => (b.disabled = !loop && i >= last));
    this.dots?.querySelectorAll('.dots__dot').forEach((d, n) => d.setAttribute('aria-current', String(n === Math.min(i, last))));
    this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: i } }));
  }
}
if (!customElements.get('cs-carousel')) customElements.define('cs-carousel', ScrollCarousel);

/* ---------- Country / language picker ---------- */
// <localization-form> wraps Shopify's localization form; any [data-value] button or select change submits it.
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.addEventListener('click', (e) => {
      const option = e.target.closest('[data-value]');
      if (!option || !this.form) return;
      e.preventDefault();
      const input = this.form.querySelector(`input[name="${option.dataset.name}"]`);
      if (input) { input.value = option.dataset.value; this.form.submit(); }
    });
    this.addEventListener('change', (e) => { if (e.target.matches('select')) this.form?.submit(); });
  }
}
if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

/* ---------- Entrance animations ---------- */
// [data-reveal] fades in when it enters the screen; product cards get a growing delay when "stagger" is on.
// Headings with [data-animate-heading] are split in lines and use the "Heading entrance" setting.
const headingMode = document.documentElement.dataset.headingAnimation || 'none';
// Stagger (live): the cards that come into view together form one batch; each gets its place in the batch
// (--reveal-index, in reading order) and the batch size (--reveal-count), which drive the delay curve in base.css.
const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  const shown = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
  const batch = shown.filter((el) => el.hasAttribute('data-stagger'))
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  batch.forEach((el, i) => { el.style.setProperty('--reveal-index', i); el.style.setProperty('--reveal-count', batch.length); });
  shown.forEach((el) => {
    el.classList.add('is-visible');
    revealObserver.unobserve(el);
  });
}, { rootMargin: '0px 0px -40px 0px' }) : null;
// Live reveals a whole product row/grid at once: when the list (the cards' parent) comes 50px into view, all its
// cards fade in as one batch (index = place in the list, count = cards in the list), on screen or not.
const revealGroup = (list) => {
  const batch = [...list.children].filter((el) => el.hasAttribute('data-stagger') && !el.classList.contains('is-visible'));
  batch.forEach((el, i) => {
    el.style.setProperty('--reveal-index', i);
    el.style.setProperty('--reveal-count', batch.length);
    el.classList.add('is-visible');
    revealObserver.unobserve(el);
  });
  list.dataset.staggerGroup = 'done';
};
const groupObserver = revealObserver ? new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    groupObserver.unobserve(entry.target);
    revealGroup(entry.target);
  });
}, { rootMargin: '-50px 0px' }) : null;

function splitLines(el) {
  if (el.dataset.split) return;
  el.dataset.split = '1';
  const words = el.textContent.trim().split(/\s+/);
  if (!words.length || el.children.length) return; // keep headings with inner markup as they are
  el.textContent = '';
  const spans = words.map((w, i) => {
    const s = document.createElement('span');
    s.textContent = w + (i < words.length - 1 ? ' ' : '');
    el.append(s);
    return s;
  });
  const lines = [];
  let top = null;
  spans.forEach((s) => { if (s.offsetTop !== top) { lines.push([]); top = s.offsetTop; } lines[lines.length - 1].push(s.textContent); });
  el.textContent = '';
  lines.forEach((words2, i) => {
    const line = document.createElement('span');
    line.className = 'split-line';
    line.style.setProperty('--line-index', i);
    line.textContent = words2.join('');
    el.append(line);
  });
}

Theme.initAnimations = (root = document) => {
  if (!revealObserver || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el, i) => {
    el.classList.add('reveal');
    if (document.body.classList.contains('stagger-products') && el.matches('.product-tile, [data-reveal="stagger"]')) {
      el.setAttribute('data-stagger', '');
      const list = el.parentElement;
      // Cards added later to a list that already played (e.g. more results) reveal on their own, batched per callback.
      if (list && list.dataset.staggerGroup !== 'done') {
        if (!list.dataset.staggerGroup) { list.dataset.staggerGroup = 'wait'; groupObserver.observe(list); }
        return;
      }
    }
    revealObserver.observe(el);
  });
  if (headingMode === 'none') return;
  root.querySelectorAll('[data-animate-heading]:not([data-split])').forEach((el) => {
    if (window.Shopify && window.Shopify.designMode) return;
    splitLines(el);
    el.classList.add('split-heading', `split-heading--${headingMode}`);
    revealObserver.observe(el);
  });
};
document.addEventListener('DOMContentLoaded', () => Theme.initAnimations());
document.addEventListener('shopify:section:load', (e) => Theme.initAnimations(e.target));
document.addEventListener('tt:navigated', () => Theme.initAnimations());

/* ---------- Header height for sticky offsets ---------- */
Theme.setHeaderHeight = () => {
  const header = document.querySelector('[data-site-header]');
  const bar = document.querySelector('[data-announcement-bar]');
  if (header) document.documentElement.style.setProperty('--header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
  document.documentElement.style.setProperty('--announcement-height', bar ? `${Math.round(bar.getBoundingClientRect().height)}px` : '0px');
};
window.addEventListener('resize', Theme.debounce(Theme.setHeaderHeight, 100));
document.addEventListener('DOMContentLoaded', Theme.setHeaderHeight);
document.addEventListener('shopify:section:load', Theme.setHeaderHeight);

/* ---------- Theme editor: open dialogs when their section is selected ---------- */
document.addEventListener('shopify:section:select', (e) => {
  const dialog = e.target.querySelector('dialog[data-editor-open]');
  if (dialog) Theme.openDialog(dialog);
});
document.addEventListener('shopify:section:deselect', (e) => {
  const dialog = e.target.querySelector('dialog[data-editor-open]');
  if (dialog) Theme.closeDialog(dialog);
});

export default Theme;
